import { useEffect, useRef, useState } from "react";

/* useActivityStream — Bloomberg-style streaming for the Activity
   ticker. Implements BACKEND_TERMINAL_SPEC §A.7 verbatim:

     1. Open WebSocket (or SSE) to wsUrl.
     2. Server backfills last ~50 events with type="tx".
     3. Server emits one sentinel { type: "live" } → flip status from
        "loading" to "streaming".
     4. Live mode: every frame { type: "tx", ts, rail, ... } is
        prepended to the buffer, capped at maxEvents.
     5. Heartbeat { type: "ping" } every 30s keeps the connection.
     6. On disconnect, exponential-backoff reconnect (1.5x, capped
        at 30s). If backend isn't reachable at all, fall back to
        the initialEvents array so the UI is never empty.

   When wsUrl is null/empty the hook stays in "fallback" mode and
   only renders initialEvents — same shape as the live frames.

   Status: 'loading' | 'streaming' | 'fallback' | 'reconnecting'

   Usage:
     const { events, status } = useActivityStream({
       wsUrl: env.REACT_APP_ACTIVITY_WS,
       token,
       initialEvents: MOCK_ACTIVITY,
     });
     <ActivityTicker events={events} status={status} /> */

const HEARTBEAT_TIMEOUT_MS = 45_000;
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
const DEFAULT_MAX_EVENTS = 200;

export default function useActivityStream({
  wsUrl,
  token,
  initialEvents = [],
  maxEvents = DEFAULT_MAX_EVENTS,
} = {}) {
  const [events, setEvents] = useState(() => initialEvents.slice(0, maxEvents));
  const [status, setStatus] = useState(wsUrl ? "loading" : "fallback");
  const wsRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const heartbeatTimerRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!wsUrl) {
      setStatus("fallback");
      return () => {};
    }

    const clearHeartbeat = () => {
      if (heartbeatTimerRef.current) {
        clearTimeout(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
    };
    const scheduleHeartbeatTimeout = () => {
      clearHeartbeat();
      heartbeatTimerRef.current = setTimeout(() => {
        if (wsRef.current) {
          try { wsRef.current.close(4000, "heartbeat-timeout"); } catch (_) { /* socket already closing */ }
        }
      }, HEARTBEAT_TIMEOUT_MS);
    };

    const connect = () => {
      if (!mountedRef.current) return;
      let ws;
      try {
        ws = token
          ? new WebSocket(wsUrl, [`bearer.${token}`])
          : new WebSocket(wsUrl);
      } catch (e) {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        reconnectAttemptRef.current = 0;
        setStatus("loading");
        scheduleHeartbeatTimeout();
      };

      ws.onmessage = (ev) => {
        if (!mountedRef.current) return;
        let frame;
        try { frame = JSON.parse(ev.data); }
        catch (_) { return; }
        scheduleHeartbeatTimeout();
        if (frame.type === "ping") return;
        if (frame.type === "live") { setStatus("streaming"); return; }
        if (frame.type === "tx") {
          setEvents((prev) => {
            const next = [frame, ...prev];
            return next.length > maxEvents ? next.slice(0, maxEvents) : next;
          });
          return;
        }
        // Unknown frame types are ignored — server is free to add
        // new ones without breaking the FE.
      };

      ws.onclose = () => {
        clearHeartbeat();
        wsRef.current = null;
        if (mountedRef.current) scheduleReconnect();
      };

      ws.onerror = () => {
        // close handler will run; nothing to do here beyond logging.
      };
    };

    const scheduleReconnect = () => {
      if (!mountedRef.current) return;
      const attempt = reconnectAttemptRef.current + 1;
      reconnectAttemptRef.current = attempt;
      setStatus("reconnecting");
      const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * Math.pow(1.5, attempt - 1));
      reconnectTimerRef.current = setTimeout(connect, delay);
    };

    connect();

    return () => {
      mountedRef.current = false;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      clearHeartbeat();
      if (wsRef.current) {
        try { wsRef.current.close(1000, "unmount"); } catch (_) { /* already closed */ }
      }
    };
  }, [wsUrl, token, maxEvents]);

  return { events, status };
}
